import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useSettingsStore } from '@/store/settingsStore';
import { 
  fetchDashboardData,
  type DashboardApiResponse,
  type DashboardQueryParams,
  type TimeSeriesData,
  type GeographicDataList,
  type PendingStepsData,
  type DeviceData,
  type BrowserData,
  type DurationPercentilesData
} from '../api/dashboard.api';

/**
 * Consolidated dashboard data type that includes all chart data
 */
export interface ConsolidatedDashboardData {
  // Raw API response
  raw: DashboardApiResponse;
  
  // Processed metrics
  metrics: {
    total_validations: number;
    successful_validations: number;
    failed_validations: number;
    pending_validations: number;
    validation_rate: number;
    unique_devices: number;
    approved_devices: number;
    device_conversion_rate: number;
    period: {
      start: string;
      end: string;
    };
  };
  
  // Chart data extracted from raw response
  charts: {
    validationsByDate: TimeSeriesData;
    geographicData: GeographicDataList;
    pendingSteps: PendingStepsData;
    deviceData: DeviceData;
    browserData: BrowserData;
    durationData: DurationPercentilesData;
  };
}

/**
 * Hook for fetching ALL dashboard data in a single query
 * This replaces multiple separate queries with one consolidated call
 */
export function useDashboardData(
  params?: DashboardQueryParams
): UseQueryResult<ConsolidatedDashboardData, Error> {
  const { selectedOrganization, selectedProject } = useSettingsStore();
  
  return useQuery({
    queryKey: [
      'dashboard', 
      'consolidated', 
      selectedOrganization?.id, 
      selectedProject?.id, 
      params
    ],
    queryFn: async () => {
      // Single API call to get all dashboard data
      const rawData = await fetchDashboardData(params);
      
      // Process metrics from raw data
      const totalValidations = rawData.statusCounter.total || 0;
      const successfulValidations = rawData.statusCounter.finished.approved || 0;
      const failedValidations = rawData.statusCounter.finished.rejected || 0;
      const pendingValidations = Object.values(rawData.statusCounter.pendings).reduce((sum, count) => sum + count, 0);
      const validationRate = totalValidations > 0 ? (successfulValidations / totalValidations) * 100 : 0;
      const uniqueDevices = rawData.statusCounter.devices?.unique ?? 0;
      const approvedDevices = rawData.statusCounter.devices?.approved ?? 0;
      const deviceConversionRate = rawData.statusCounter.devices?.conversionRate ?? 0;

      // Create period based on params or default to current month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const metrics = {
        total_validations: totalValidations,
        successful_validations: successfulValidations,
        failed_validations: failedValidations,
        pending_validations: pendingValidations,
        validation_rate: Math.round(validationRate * 100) / 100,
        unique_devices: uniqueDevices,
        approved_devices: approvedDevices,
        device_conversion_rate: deviceConversionRate,
        period: {
          start: params?.startDate || startOfMonth.toISOString(),
          end: params?.endDate || endOfMonth.toISOString(),
        },
      };
      
      // Process chart data from raw response
      const charts = {
        // Validations by date chart
        validationsByDate: {
          labels: rawData.validationsByDate?.date || [],
          data: rawData.validationsByDate?.totalData?.map(value => value ?? 0) || [],
          title: 'Validations by Date',
          timeseries: true,
        } as TimeSeriesData,
        
        // Geographic data
        geographicData: (rawData.geolocations || [])
          .map((item: any, index: number) => {
            if (!item || typeof item !== 'object') return null;
            
            let coordinates = { lat: 0, lng: 0 };
            if (item.coordinates) {
              coordinates = {
                lat: Number(item.coordinates.lat) || 0,
                lng: Number(item.coordinates.lng) || 0,
              };
            } else if (item.lat !== undefined && item.lng !== undefined) {
              coordinates = {
                lat: Number(item.lat) || 0,
                lng: Number(item.lng) || 0,
              };
            }
            
            // Validate coordinates
            if (coordinates.lat < -90 || coordinates.lat > 90 || 
                coordinates.lng < -180 || coordinates.lng > 180) {
              return null;
            }
            
            return {
              id: item.id || `geo-${index}`,
              coordinates,
              country: item.country || item.country_name || null,
              country_code: item.country_code || item.countryCode || null,
              validations: Number(item.validations) || Number(item.count) || 0,
            };
          })
          .filter(Boolean) as GeographicDataList,
        
        // Pending steps chart
        pendingSteps: {
          data: Object.entries(rawData.statusCounter.pendings).map(([key, value]) => ({
            name: key.charAt(0).toUpperCase() + key.slice(1),
            value,
          })),
          title: 'Verification Steps Breakdown',
          type: 'bar',
        } as PendingStepsData,
        
        // Device data chart
        deviceData: {
          data: rawData.devicesCounter
            .sort((a: any, b: any) => b.count - a.count)
            .slice(0, 6)
            .map((device: any) => ({
              name: device.deviceName.length > 20 
                ? device.deviceName.substring(0, 20) + '...' 
                : device.deviceName,
              value: device.count,
            })),
          title: 'Top Devices Used',
          type: 'pie',
        } as DeviceData,
        
        // Browser data chart
        browserData: {
          data: rawData.browsersCounter.map((browser: any) => ({
            name: browser.browserName,
            value: browser.count,
          })),
          title: 'Browsers Used',
          type: 'pie',
        } as BrowserData,
        
        // Duration data chart
        durationData: {
          data: rawData.validationDuration.map(item => ({
            name: item.name.toUpperCase(),
            value: parseFloat(item.value),
          })),
          title: 'Validation Duration Percentiles',
          type: 'bar',
          unit: 'seconds',
        } as DurationPercentilesData,
      };
      
      return {
        raw: rawData,
        metrics,
        charts,
      };
    },
    enabled: !!selectedOrganization && !!selectedProject,
    staleTime: 0, // Always consider data stale for immediate refresh
    gcTime: 10 * 60 * 1000, // 10 minutes cache time
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });
}

/**
 * Hook for getting dashboard status from consolidated data
 */
export function useDashboardStatus(params?: DashboardQueryParams) {
  const { data, isLoading, isError, isSuccess, error, refetch } = useDashboardData(params);
  
  const isEmpty = isSuccess && !!data && data.metrics.total_validations === 0;
  const hasData = isSuccess && !!data && data.metrics.total_validations > 0;
  
  return {
    isLoading,
    isError,
    isEmpty,
    isSuccess,
    hasData,
    error: error || null,
    retry: () => refetch(),
    data: data?.metrics,
  };
}